import { useAtom } from '@reatom/npm-react'
import { lazy } from 'react'
import { Route, Routes } from 'react-router'
import { createGlobalStyle } from 'styled-components'

import { UserStatus } from '@root/entities/auth'
import { TerminalAccessGate } from '@root/features/access'
import { AuthenticatedOnly } from '@root/features/auth/authenticate'
import {
  AuthRoute,
  FunExploreRoute,
  NftRoute,
  OrdersRoute,
  PortfolioV2Route,
  PremiumRoute,
  ProfileReferralLeaderboardRoute,
  ProfileReferralUsersRoute,
  ProfileRoute,
  ProfileTradingLeaderboardRoute,
  PulseV2Route,
  RootRoute,
  TradeRoute,
  TradeV2Route,
} from '@root/features/routing'
import {
  WalletManagerRoute,
  WalletTrackerRoute,
} from '@root/features/routing/model'
import { AuthPage } from '@root/pages/auth/auth-page'
import { BackOfficePage } from '@root/pages/back-office/page/back-office-page'
import { NotFoundPage } from '@root/pages/common'
import { ExplorePage } from '@root/pages/explore'
import { ExploreV2Page } from '@root/pages/explore-v2'
import { PortfolioV2Page } from '@root/pages/portfolio-v2'
import { PremiumPage } from '@root/pages/premium/premium-page'
import { ProfilePage } from '@root/pages/profile'
import {
  ProfileBoard,
  ReferralLeaderboardBoard,
  ReferralUsersBoard,
  TradingLeaderboardBoard,
} from '@root/pages/profile/board'
import { OrdersPage } from '@root/pages/trade/orders-page/orders-page'
import { TradePage } from '@root/pages/trade/trade-page/trade-page'
import { TradeV2Page } from '@root/pages/trade-v2'
import { WalletManagerPage } from '@root/pages/wallet-manager/ui'
import { WalletTrackerPage } from '@root/pages/wallet-tracker/ui'
import { PREMIUM_ENABLED } from '@root/shared'
import { isTauriAtom } from '@root/shared/core'
import { DialogsProvider } from '@root/shared/ui-kit'
import { DesktopLayout } from '@root/widgets/layout/desktop-layout'

import { useDeeplinkHandlers } from '../common/use-deeplink-handlers'

import { DesktopAppDialogs } from './desktop-app-dialogs'

const FunExplorePageLazy = lazy(() =>
  import('@root/pages/fun/explore-page/ui/fun-explore-page').then((it) => ({
    default: it.FunExplorePage,
  })),
)
const NftPageLazy = lazy(() =>
  import('@root/pages/nft/ui/nft-page').then((it) => ({
    default: it.NftPage,
  })),
)

export const DesktopApp = () => {
  const [isTauri] = useAtom(isTauriAtom)

  useDeeplinkHandlers()

  return (
    <DialogsProvider>
      <DesktopGlobalStyles withTitleBar={isTauri} />

      <Routes>
        <Route
          path={AuthRoute.path}
          element={<AuthPage withTitleBar={isTauri} />}
        />
        {/*
          Pulse V2 draws its own chrome, so it sits outside DesktopLayout —
          inside the same invite gate, but with none of the old shell around it.
        */}
        <Route
          path={PulseV2Route.path}
          element={
            <TerminalAccessGate>
              <ExploreV2Page />
            </TerminalAccessGate>
          }
        />
        {/* The account screen draws its own chrome too, for the same reason. */}
        <Route
          path={PortfolioV2Route.path}
          element={
            <TerminalAccessGate>
              <PortfolioV2Page />
            </TerminalAccessGate>
          }
        />
        <Route
          path={TradeV2Route.path}
          element={
            <TerminalAccessGate>
              <TradeV2Page />
            </TerminalAccessGate>
          }
        />
        <Route
          path={RootRoute.path}
          element={
            // Invite-only: the whole terminal sits behind this, every route
            // below included. Sign-in above it stays reachable.
            <TerminalAccessGate>
              <DesktopLayout withTitleBar={isTauri} />
            </TerminalAccessGate>
          }
        >
          <Route path={NftRoute.path} element={<NftPageLazy />} />
          <Route index element={<ExplorePage />} />
          {/* Premium is hidden until it is sold; its routes go with it. */}
          {PREMIUM_ENABLED ? (
            <Route path={PremiumRoute.path} element={<PremiumPage />} />
          ) : undefined}
          <Route path={TradeRoute.path} element={<TradePage />} />
          <Route
            path={OrdersRoute.path}
            element={
              <AuthenticatedOnly>
                <OrdersPage />
              </AuthenticatedOnly>
            }
          />
          <Route
            path={WalletTrackerRoute.path}
            element={
              <AuthenticatedOnly>
                <WalletTrackerPage />
              </AuthenticatedOnly>
            }
          />
          <Route
            path={WalletManagerRoute.path}
            element={
              <AuthenticatedOnly>
                <WalletManagerPage />
              </AuthenticatedOnly>
            }
          />

          <Route path={FunExploreRoute.path}>
            <Route index element={<FunExplorePageLazy />} />
          </Route>

          <Route path="/admin">
            <Route
              index
              element={
                <AuthenticatedOnly userStatusesOnly={[UserStatus.Admin]}>
                  <BackOfficePage />
                </AuthenticatedOnly>
              }
            />
          </Route>
          <Route
            path={ProfileRoute.path}
            element={
              <AuthenticatedOnly>
                <ProfilePage />
              </AuthenticatedOnly>
            }
          >
            <Route index element={<ProfileBoard />} />
            <Route
              path={ProfileReferralLeaderboardRoute.path}
              element={<ReferralLeaderboardBoard />}
            />
            <Route
              path={ProfileTradingLeaderboardRoute.path}
              element={<TradingLeaderboardBoard />}
            />
            <Route
              path={ProfileReferralUsersRoute.path}
              element={<ReferralUsersBoard />}
            />
          </Route>

          <Route path="/404" element={<NotFoundPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>

      <DesktopAppDialogs />
    </DialogsProvider>
  )
}

const TITLE_BAR_HEIGHT = 38

const DesktopGlobalStyles = createGlobalStyle<{
  withTitleBar: boolean
}>`
  :root {
    --title-bar-height: ${({ withTitleBar }) => (withTitleBar ? TITLE_BAR_HEIGHT : 0)}px;
    --viewport-height: calc(100vh - var(--title-bar-height));
  }

`
